import React from 'react';

import ClickEventLoggerWrapper from '@/components/common/ClickEventLogWrapper';
import InfiniteScroll from '@/components/common/infinite-scroll';
import LinkTag from '@/components/common/link-tag';
import HospitalSkeletonList from '@/components/templates/f2f/common/hospital-list/hospital-skeleton-list';
import MedicineDictionary from '@/entities/medicine-dictionary/ui/MedicineDictionary';
import { createMedicineDictionaryDetailUrlWithKeyword } from '@/entities/medicine-dictionary/util';
import { SearchMedicineDictionaryViewModel } from '@/hooks/search/medicine-dictionary/useSearchMedicineDictionaryViewModel';
import EmptySearchResult from '../empty-search-result';

interface Props {
  medicineDictionaryViewModel: SearchMedicineDictionaryViewModel;
}

const MedicineDictionarySearchResult = ({ medicineDictionaryViewModel }: Props) => {
  if (medicineDictionaryViewModel.isMedicineDictionariesLoading) {
    return <HospitalSkeletonList />;
  }

  if (!medicineDictionaryViewModel.medicineDictionaries.length) {
    return <EmptySearchResult handleClickRequestButton={medicineDictionaryViewModel.handleClickRequestButton} />;
  }

  return (
    <>
      <InfiniteScroll
        rootMargin={'0px 0px 0px 100px'}
        isLoading={medicineDictionaryViewModel.isMedicineDictionariesLoading}
        hasNextPage={medicineDictionaryViewModel.hasMedicineDictionariesNextPage}
        fetchNextPage={medicineDictionaryViewModel.fetchMedicineDictionariesNextPage}
      >
        <div className='[&>*:not(:last-child)>div]:border-gray-200 [&>*:not(:last-child)>div]:border-b'>
          {medicineDictionaryViewModel.medicineDictionaries.map((medicineDictionary, index) => (
            <ClickEventLoggerWrapper
              key={`medicineDictionary_${medicineDictionary.getPublicId()}`}
              onClickEventLog={() =>
                medicineDictionaryViewModel.onClickLogMedicineDictionary(medicineDictionary.getPublicId())
              }
            >
              <>
                <div className='flex flex-col gap-[12px] p-[24px]'>
                  <LinkTag
                    href={createMedicineDictionaryDetailUrlWithKeyword(
                      medicineDictionary.getPublicId(),
                      medicineDictionaryViewModel.keyword
                    )}
                    onClickNavigateToScreen={medicineDictionaryViewModel.onClickMedicineDictionary(
                      medicineDictionary.getPublicId()
                    )}
                  >
                    <MedicineDictionary
                      title={medicineDictionary.getBasicInfoName()}
                      description={medicineDictionary.getPharmaceuticalEffectLiteracy()}
                      summary={medicineDictionary.getWholeSummary()}
                      image={medicineDictionary.getRepresentativeImage()}
                      imageAlt={medicineDictionary.getSimplifiedName()}
                    />
                  </LinkTag>
                </div>
                {index !== medicineDictionaryViewModel.medicineDictionaries.length - 1 && (
                  <div className='h-[1px] w-full bg-gray-40' />
                )}
              </>
            </ClickEventLoggerWrapper>
          ))}
        </div>
      </InfiniteScroll>
    </>
  );
};

export default MedicineDictionarySearchResult;
